import type { View } from "@novorender/api";
import type { BoundingSphere, HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3 } from "gl-matrix";

// Deep imports rather than the `apis/procore` barrel, which pulls in the RTK Query API
// and the analytics client (and through them the store) for two endpoint modules — the
// same reason `pickDataAccess` is kept out of the `dataAccess` barrel.
import {
    BIM_QUERY_BOUNDS_ID_LIMIT,
    BIM_QUERY_EXPAND_ID_LIMIT,
    BIM_QUERY_OBJECT_PAGE_LIMIT,
    BIM_QUERY_ROOT,
    BIM_QUERY_TREE_PAGE_LIMIT,
    type BimQueryBoundsParts,
    type BimQueryClimbPolicy,
    type BimQueryIdSet,
    type BimQueryObject,
    type BimQueryTarget,
    climbBimQueryObjects,
    expandBimQueryIdSet,
    getBimQueryBounds,
    getBimQueryObject,
    listBimQueryAncestors,
    listBimQueryChildren,
    listBimQueryDescendantIdSet,
    resolveBimQuerySelectionIdSet,
    searchBimQueryIdSet,
    searchBimQueryObjects,
} from "apis/procore/bimQuery";
import type { SearchPredicate } from "apis/procore/searchPredicate";
import {
    type BoundsOptions,
    type BoundsParts,
    byProperty,
    type FindIdsOptions,
    objectBoundsAsSelection,
    type ObjectDataAccess,
    ObjectMetadataCache,
    type PropertyQuery,
    type PropertyTerm,
    resolveSelectionLegacy,
    type SelectionBounds,
    type SelectionResolutionMode,
    type SelectionResolutionOptions,
    type SelectionTarget,
    singleSelectionTarget,
} from "dataAccess";
import { NodeType } from "types/misc";
import { log } from "utils/log";
import { mergeBoundingSpheres, unionAABB3 } from "utils/math";

const l = log.dataAccess.bimIndex;

import { objectNodeClassPredicate, queryPredicate } from "./predicate";
import { MAX_PREDICATE_VALUES, restPredicate } from "./restPredicate";
import { bimIndexBitmapIds } from "./session";

/**
 * How many times a search may be resumed before this stops asking.
 *
 * A cut-short scan hands back a token that continues it, and continuing can itself be
 * cut short — so the honest read is a loop. The bound is what keeps a scene whose
 * scans never complete from looping forever: past it the set is reported incomplete,
 * which is what it would have been anyway, instead of never being reported at all.
 */
const MAX_RESUMED_READS = 10;

/**
 * How many resolved clicks are remembered.
 *
 * An entry count rather than the byte budget `ObjectMetadataCache` needs: class-aware
 * resolution climbs to an *object*, whose subtree is bounded in a way a file's or a
 * container's is not, so the ids an entry holds cannot run away the way
 * {@link SelectionTarget.highlightIds} warns a highlight set in general can.
 */
const MAX_SELECTION_RESOLUTION_ENTRIES = 100;

async function* emptyIterator(): AsyncIterableIterator<HierarcicalObjectReference> {}

/**
 * A resolved click and whether the index narrowed anything answering it — which is what
 * says the answer is safe to remember, and so never leaves this module.
 */
type SelectionResolution = { target: SelectionTarget; degraded: boolean };

/**
 * A resolution taken as complete. Legacy resolution reads its subtree through
 * {@link ObjectDataAccess.getDescendantIds}, which reports a narrowed read to the log
 * rather than to its caller, so there is nothing to carry here.
 */
function complete(target: SelectionTarget | undefined): SelectionResolution | undefined {
    return target && { target, degraded: false };
}

/** Why a read narrowed, as a log suffix — the bitmap reads name their causes. */
function causes(set: BimQueryIdSet): string {
    return set.degradations.length ? ` (${set.degradations.join(", ")})` : "";
}

/** The API spells its enums SCREAMING_SNAKE_CASE; the interface spells them lowercase. */
const WIRE_BOUNDS_PARTS: Record<BoundsParts, BimQueryBoundsParts> = {
    box: "BOX",
    sphere: "SPHERE",
    both: "BOTH",
};

/**
 * The two resolution modes are the two climb policies, under the names each side uses:
 * the shallowest matching ancestor is the "first object", the deepest is the "last".
 */
const CLIMB_POLICIES: Record<SelectionResolutionMode, BimQueryClimbPolicy> = {
    "first-object": "TOPMOST",
    "last-object": "NEAREST",
};

/** The path a child hangs off its parent, or its own name at the top of the tree. */
function childPath(parent: HierarcicalObjectReference | undefined, name: string): string {
    return parent ? `${parent.path}/${name}` : name;
}

/**
 * The API's `has_children` as the viewer's node type: a node with children is a grouping
 * row, and one without is a leaf. Every row and every object the API answers with carries
 * the flag, so this is the only place the two vocabularies meet.
 */
function nodeType(hasChildren: boolean): ObjectData["type"] {
    return hasChildren ? NodeType.Internal : NodeType.Leaf;
}

/**
 * An index object as `ObjectData`. Read-only, as everything on the Procore backend is.
 *
 * The node type comes off the object's own `has_children`, so an object read is as
 * informative about it as a tree row is — see {@link nodeType}.
 */
function toObjectData(object: BimQueryObject): ObjectData {
    const data: ObjectData = {
        id: object.id,
        name: object.name,
        path: object.path,
        type: nodeType(object.hasChildren),
        properties: object.properties,
        bounds: object.bounds,
        async loadMetaData() {
            return data;
        },
        async save() {
            return false;
        },
    };
    return data;
}

/**
 * The same query, split so that no single term is wider than the API will plan.
 *
 * Callers legitimately ask about a whole selection — `predicate.ts` spends hundreds of
 * thousands of ids as an `OR` of `IN` chunks for exactly that — but a served index has a
 * branch-width ceiling a local one does not (see {@link MAX_PREDICATE_VALUES}), so past
 * it the query has to become several requests. Splitting the *query* rather than the
 * predicate keeps `predicate.ts` the only place that knows how a term becomes a
 * predicate.
 *
 * Recursive, so a query with two over-wide terms is covered rather than half-covered:
 * the union over every combination of batches is the original conjunction.
 */
function splitWideTerms(query: PropertyQuery): PropertyQuery[] {
    const wide = query.terms.findIndex((term) => (term.anyOf?.length ?? 0) > MAX_PREDICATE_VALUES);
    if (wide === -1) {
        return [query];
    }
    const values = query.terms[wide]!.anyOf!;
    const batches: PropertyQuery[] = [];
    for (let from = 0; from < values.length; from += MAX_PREDICATE_VALUES) {
        const batch = values.slice(from, from + MAX_PREDICATE_VALUES);
        // Rebuilt rather than spread over: `PropertyTerm` is a union of exactly one
        // match kind, and only the `anyOf` kind can be over-wide.
        const terms = query.terms.map(
            (term, at): PropertyTerm =>
                at === wide ? { property: term.property, negate: term.negate, anyOf: batch } : term,
        );
        batches.push(...splitWideTerms({ terms }));
    }
    return batches;
}

/**
 * Data access backed by a BIM index **served over the network** — Procore's BIM Query
 * API (`apis/procore/bimQuery`).
 *
 * The sibling `BimIndexOfflineDataAccess` anticipated: the same index, the same query
 * algebra and the same `predicate.ts` translation, but answered by a service that has
 * the files mounted rather than by a worker reading OPFS. That makes it available to
 * every scene rather than only a synced one, which is why it sits between the downloaded
 * index and the object DB in `pickDataAccess`.
 *
 * It is a separate implementation from the offline one because the two differ in exactly
 * what the interface asks about: `isIndexAvailableOffline` is unconditionally `true`
 * there and `false` here. Nothing else about the answers differs.
 *
 * Object *properties* come from the scene's own metadata (`view.data`) when the scene
 * carries any, and from the index's own object read otherwise — unlike the offline
 * backend, which stops at the scene because reaching for a server would defeat its
 * purpose. One consequence of the API's row shapes is worth knowing rather than
 * discovering: the tree rows carry a **name but no path**, so paths are composed from the
 * parent's as the tree is walked ({@link childPath}). Sound because a name is by
 * definition the leaf segment of a path, and it is how {@link getAncestors} reconstructs
 * a breadcrumb too.
 *
 * No request is made at construction, so choosing this backend at startup costs nothing
 * until it is asked something.
 */
export class BimIndexDataAccess implements ObjectDataAccess {
    readonly kind = "bim-index" as const;

    private readonly metadataCache = new ObjectMetadataCache();

    /** Iteration order is LRU order: entries are re-inserted on access. */
    private readonly selectionResolutionCache = new Map<string, SelectionTarget>();

    constructor(
        private readonly view: View,
        private readonly target: BimQueryTarget,
    ) {}

    isIndexAvailableOffline() {
        return false;
    }

    isDataAvailableOffline() {
        return Boolean(this.view.data);
    }

    /**
     * The scene's own metadata when it has any, otherwise one object read from the index.
     *
     * A bare id is enough for the node type too: the object read answers `has_children`,
     * so this reports the same `Internal`/`Leaf` a tree row would for the same id.
     */
    getObject(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined> {
        return this.object(id, signal);
    }

    /**
     * The first object at `path`, found by an equality query on the index's `$path`.
     *
     * One read: the page is asked for at `view=FULL`, so its row arrives with the
     * properties an object read would have gone back for. Affordable here where it is not
     * for {@link matches} because the page is one row wide. The object goes into the
     * metadata cache on the way past, as {@link indexObject}'s own reads do.
     */
    async getObjectAtPath(path: string, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const predicate = queryPredicate(byProperty("path", [path]));
        if (!predicate) {
            return undefined;
        }
        const page = await this.unlessAborted(signal, () =>
            searchBimQueryObjects(this.target, {
                predicate: restPredicate(predicate),
                page: 1,
                perPage: 1,
                view: "FULL",
                signal,
            }),
        );
        const first = page?.items[0];
        if (!first) {
            return undefined;
        }
        const object = toObjectData(first);
        this.metadataCache.put(object.id, object);
        return object;
    }

    /**
     * The nearest element of the object's ancestor chain. The API has no separate parent
     * endpoint on purpose — the chain is answered nearest-first, so its head *is* the
     * parent, and asking for it costs no more than a dedicated call would.
     */
    async getParent(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<ObjectData | undefined> {
        const chain = await this.unlessAborted(signal, () =>
            listBimQueryAncestors(this.target, { id: object.id, signal }),
        );
        const parent = chain?.[0];
        return parent && this.object(parent.id, signal);
    }

    /**
     * A node's children, or the top-level nodes when given none.
     *
     * Keyset-paged: each page names the cursor that continues it, and the cursor never
     * leaves this class — so a caller that stops early stops the requests.
     */
    async *getChildren(
        parent: HierarcicalObjectReference | undefined,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        for (let cursor: string | null = null; ; ) {
            const page = await this.unlessAborted(signal, () =>
                listBimQueryChildren(this.target, {
                    id: parent?.id ?? BIM_QUERY_ROOT,
                    cursor,
                    perPage: BIM_QUERY_TREE_PAGE_LIMIT,
                    signal,
                }),
            );
            if (!page) {
                return;
            }
            for (const node of page.items) {
                yield this.reference(node.id, childPath(parent, node.name), node.hasChildren);
            }
            cursor = page.nextCursor;
            if (!cursor) {
                return;
            }
        }
    }

    /** One predicate over the query's terms, paged as the iterator is consumed. */
    findByProperties(query: PropertyQuery, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference> {
        return query.terms.length ? this.matchesOf(query, signal) : emptyIterator();
    }

    async findIdsByProperties(query: PropertyQuery, options?: FindIdsOptions): Promise<Uint32Array> {
        const { includeDescendants, signal } = options ?? {};
        if (!query.terms.length) {
            return new Uint32Array();
        }

        const matches = await this.matchIdSets(query, signal);
        if (!matches?.length) {
            // Aborted, or a query no term of which could be translated — either way
            // there is nothing to decode, so the worker is not woken for it.
            return new Uint32Array();
        }
        // Materialized here even when the subtrees are wanted: `expand` takes its roots
        // as a JSON array, so this is the one set that has to leave the worker to be
        // asked about.
        const roots = await bimIndexBitmapIds(matches.map((set) => set.bytes));
        if (signal?.aborted || !includeDescendants || !roots.length) {
            return signal?.aborted ? new Uint32Array() : roots;
        }

        const expanded: BimQueryIdSet[] = [];
        for (let from = 0; from < roots.length; from += BIM_QUERY_EXPAND_ID_LIMIT) {
            const batch = roots.subarray(from, from + BIM_QUERY_EXPAND_ID_LIMIT);
            const set = await this.unlessAborted(signal, () =>
                expandBimQueryIdSet(this.target, { ids: Array.from(batch), includeSelf: false, signal }),
            );
            if (!set) {
                return new Uint32Array();
            }
            if (set.degraded) {
                // Rows returned are correct; the set covers fewer objects than the roots
                // stand for, with nothing to name what is missing.
                l.warn(`degraded expansion; some subtrees may be incomplete${causes(set)}`);
            }
            expanded.push(set);
        }

        // The roots are unioned in here, which is what `includeSelf: false` above leaves
        // to this side: they are already materialized, so it costs one native op.
        const ids = await bimIndexBitmapIds(
            expanded.map((set) => set.bytes),
            roots,
        );
        return signal?.aborted ? new Uint32Array() : ids;
    }

    /**
     * `CONTAINS $name` — substring on the name column, as
     * `BimIndexOfflineDataAccess.searchByName` does and for the same reasons.
     *
     * It is the operator that means what the method does: every row it returns is a name
     * match, so there is nothing to filter here and nothing to scan past, and it matches
     * mid-token so "acade" finds "Facade". `SEARCH` cannot stand in for it — it does not
     * look at the object name at all.
     *
     * The cost, here as there, is that `CONTAINS` is not an accelerated column: it is a
     * scan over the name dictionary, so a broad term on a large model can exhaust the
     * engine's query budget. Served, that at least gets *said* — the API returns a
     * `degraded` flag and {@link matches} warns on it, where the offline engine comes back
     * quietly short.
     */
    searchByName(term: string, signal?: AbortSignal): AsyncIterableIterator<HierarcicalObjectReference> {
        const needle = term.trim();
        if (!needle) {
            return emptyIterator();
        }
        // `case_sensitive` is spelled out rather than left to the service's default: the
        // search box has always been case-insensitive, and the DTO's own note says the
        // flag "may be ignored by the backend API".
        return this.matches({ operator: "CONTAINS", property: "$name", value: needle, case_sensitive: false }, signal);
    }

    /**
     * The chain in one request, root-first.
     *
     * The API answers nearest-first, which is the order a breadcrumb is built in but the
     * reverse of what this interface promises. Paths are composed on the way down, so the
     * result is self-consistent without depending on the caller's own path being
     * well-formed.
     */
    async getAncestors(
        object: HierarcicalObjectReference,
        signal?: AbortSignal,
    ): Promise<HierarcicalObjectReference[]> {
        const chain = await this.unlessAborted(signal, () =>
            listBimQueryAncestors(this.target, { id: object.id, signal }),
        );
        if (!chain) {
            return [];
        }
        const references: HierarcicalObjectReference[] = [];
        let path = "";
        for (const node of chain.toReversed()) {
            path = path ? `${path}/${node.name}` : node.name;
            references.push(this.reference(node.id, path, node.hasChildren));
        }
        return references;
    }

    async getDescendantIds(object: HierarcicalObjectReference, signal?: AbortSignal): Promise<Uint32Array> {
        const set = await this.unlessAborted(signal, () =>
            listBimQueryDescendantIdSet(this.target, { id: object.id, signal }),
        );
        if (!set) {
            // Aborted. The interface answers an abort with the empty value rather than
            // with however much of the subtree had arrived.
            return new Uint32Array();
        }
        if (set.degraded) {
            l.warn(`degraded subtree read; some descendants may be missing${causes(set)}`);
        }
        const ids = await bimIndexBitmapIds([set.bytes]);
        return signal?.aborted ? new Uint32Array() : ids;
    }

    /**
     * Nothing, deliberately — for `BimIndexOfflineDataAccess`'s reason and a louder one.
     *
     * That backend declines because the field this method fills pins a plain array to a
     * reference the tree recreates constantly, with none of `dataAccess/objectDbDescendants`'
     * shrinking and caching. Here every subtree would additionally be a request, and the
     * caller of a warm-up is by contract running over hundreds of nodes. A `false` is not
     * "no descendants": it says the ids are not on the object, and
     * {@link getDescendantIds} is how to get them.
     */
    warmDescendantIds(_object: HierarcicalObjectReference, _signal?: AbortSignal): Promise<boolean> {
        return Promise.resolve(false);
    }

    /**
     * `objects/bounds` — one request for the whole selection, folded by the service out
     * of the index's `$bounds/*` columns rather than out of rows this would otherwise
     * have to read one at a time.
     *
     * Batched only because the request body is, and folding the batches is sound: a
     * volume enclosing each part encloses the whole. The sphere fold is the same
     * approximation `mergeBoundingSpheres` makes everywhere else, so batching can widen
     * the radius slightly — invisible at a camera, and the batch size is far above any
     * real selection anyway.
     */
    async getBounds(ids: Iterable<ObjectId>, options?: BoundsOptions): Promise<SelectionBounds | undefined> {
        const { parts = "sphere", signal } = options ?? {};
        const idArr = Array.isArray(ids) ? ids : Array.from(ids);
        if (!idArr.length) {
            return undefined;
        }

        if (idArr.length === 1) {
            const obj = await this.getObject(idArr[0], signal);
            return objectBoundsAsSelection(obj?.bounds, parts);
        }

        const spheres: BoundingSphere[] = [];
        const boxes: { min: ReadonlyVec3; max: ReadonlyVec3 }[] = [];

        const combineResult = () => {
            const bounds: SelectionBounds = {};
            const sphere = mergeBoundingSpheres(spheres);
            if (sphere) {
                bounds.sphere = sphere;
            }
            const box = unionAABB3(boxes);
            if (box) {
                bounds.box = box;
            }
            return bounds.sphere || bounds.box ? bounds : undefined;
        };

        if (idArr.length < ObjectMetadataCache.MAX_ENTRIES && idArr.every((id) => this.metadataCache.has(id))) {
            // Take bounds from cache if all the objects are cached already.
            // No need to batch the promises because they are in flight or resolved already.
            try {
                for (const id of idArr) {
                    const obj = await this.indexObject(id);
                    if (obj?.bounds) {
                        if (obj.bounds.sphere && parts !== "box") {
                            spheres.push(obj.bounds.sphere);
                        }
                        if (obj.bounds.box && parts !== "sphere") {
                            boxes.push(obj.bounds.box);
                        }
                    }
                }
                return combineResult();
            } catch (e) {
                l.error("error getting bounds from cache", e);
            }
        }

        for (let from = 0; from < idArr.length; from += BIM_QUERY_BOUNDS_ID_LIMIT) {
            const result = await this.unlessAborted(signal, () =>
                getBimQueryBounds(this.target, {
                    ids: idArr.slice(from, from + BIM_QUERY_BOUNDS_ID_LIMIT),
                    parts: WIRE_BOUNDS_PARTS[parts],
                    signal,
                }),
            );
            if (!result) {
                return undefined;
            }
            if (result.degraded) {
                // Unlike a degraded search, this cannot have narrowed the volume: the
                // engine answers no bounds rather than partial ones. It says which ids it
                // could not place, which is worth knowing when a camera lands short.
                l.warn("degraded bounds; some of the selection may not have been included");
            }
            if (result.bounds?.sphere) {
                spheres.push(result.bounds.sphere);
            }
            if (result.bounds?.box) {
                boxes.push(result.bounds.box);
            }
        }

        return combineResult();
    }

    /**
     * What a click selects, memoised per picked id — a click and the right-click that
     * opens its context menu resolve the same id, as does every re-click on an object the
     * user is working with.
     *
     * Keyed on the mode and on `classAware` as well as the id: the two modes climb to
     * different ancestors for the same click, and the two resolutions answer with
     * different objects entirely, so an entry written under one must never answer for
     * the other.
     *
     * A degraded answer is not remembered. Its highlight set is a partial subtree, and
     * an entry for it would keep handing that partial set to every later click on the
     * same id — geometry that stays unhighlighted until the entry is evicted.
     *
     * The entry is handed out as-is rather than copied. Sound because {@link SelectionTarget}
     * is read-only by contract — the highlight reducer takes its ids as an `Iterable` and
     * builds its own set — and because `mainObject` is the same shared `ObjectData` the
     * metadata cache already hands to every other caller.
     */
    async resolveSelection(id: ObjectId, options: SelectionResolutionOptions): Promise<SelectionTarget | undefined> {
        const { mode, classAware, signal } = options;

        // Answered without reading anything, so an entry for it would only evict one that
        // cost a request.
        if (!classAware && mode === "last-object") {
            return singleSelectionTarget(id);
        }

        if (signal?.aborted) {
            // Checked before the lookup rather than only around the requests: a hit would
            // otherwise answer a superseded click where a miss answers `undefined`.
            return undefined;
        }

        const key = `${mode}:${classAware ? "class" : "legacy"}:${id}`;
        const cached = this.selectionResolutionCache.get(key);
        if (cached) {
            this.selectionResolutionCache.delete(key);
            this.selectionResolutionCache.set(key, cached);
            return cached;
        }

        const resolution = classAware
            ? await this.resolveSelectionByNodeClass(id, mode, signal)
            : await this.resolveSelectionWithoutNodeClass(id, mode, signal).then(complete);
        if (!resolution) {
            // An abort, or an id the index cannot place. Neither is an answer, and the
            // interface does not distinguish them — so neither is remembered.
            return undefined;
        }
        if (!resolution.degraded) {
            this.rememberSelectionResolution(key, resolution.target);
        }
        return resolution.target;
    }

    private rememberSelectionResolution(key: string, target: SelectionTarget) {
        this.selectionResolutionCache.set(key, target);
        if (this.selectionResolutionCache.size > MAX_SELECTION_RESOLUTION_ENTRIES) {
            const oldest = this.selectionResolutionCache.keys().next();
            if (!oldest.done) {
                this.selectionResolutionCache.delete(oldest.value);
            }
        }
    }

    /**
     * `climb` and `selection`, **concurrently** — one round trip for the whole answer.
     *
     * Both take the same body and both are keyed on the picked id, so neither waits on the
     * other: `climb` answers *which* object (`view=FULL`, so its properties come too), and
     * `selection` answers that same climb expanded over the object's subtree, as a bitmap.
     * Composing `expand` on top of `climb` here would mean paging the ancestor out just to
     * send it back in.
     *
     * The resolved object goes into the metadata cache on the way past — the panels that
     * open on a new selection read it next.
     *
     * A climb landing on nothing falls back to legacy, so class-aware never resolves *less*
     * than pre-`NodeClass` would have; the two spent requests are the price of not
     * serializing the common case.
     */
    private async resolveSelectionByNodeClass(
        id: ObjectId,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionResolution | undefined> {
        const request = {
            ids: [id],
            targetPredicate: restPredicate(objectNodeClassPredicate()),
            // A picked node that is itself an object answers for itself rather than
            // climbing past to whichever object encloses it.
            includeSelf: true,
            policy: CLIMB_POLICIES[mode],
            signal,
        } as const;

        const [climbed, resolved] = await Promise.all([
            // One input, so the union is one ancestor — a page of one is the whole answer.
            this.unlessAborted(signal, () => climbBimQueryObjects(this.target, { ...request, perPage: 1 })),
            this.unlessAborted(signal, () => resolveBimQuerySelectionIdSet(this.target, request)),
        ]);
        if (!climbed || !resolved) {
            return undefined;
        }

        const target = climbed.items[0];
        if (!target) {
            // A cut-short climb may have missed the object it was meant to land on, so the
            // fallback carries its degradation rather than passing for a clean answer.
            const fallback = await this.resolveSelectionWithoutNodeClass(id, mode, signal);
            return fallback && { target: fallback, degraded: climbed.degraded };
        }
        const degraded = climbed.degraded || resolved.degraded;
        if (degraded) {
            // The object is right; the highlight set may cover less of its subtree than it
            // stands for, which shows up as geometry that stays unhighlighted.
            l.warn(`degraded selection resolution for ${id}; the highlight set may be incomplete${causes(resolved)}`);
        }

        const object = toObjectData(target);
        this.metadataCache.put(object.id, object);

        // The resolved object unioned in rather than trusted to be in the bitmap: `expand`
        // contributes nothing for an id that owns no tree node, and a selection missing the
        // object it selected highlights nothing where an object-DB click highlights one id.
        const highlightIds = await bimIndexBitmapIds([resolved.bytes], new Uint32Array([object.id]));
        return signal?.aborted
            ? undefined
            : {
                  target: { mainObjectId: object.id, mainObject: object, highlightIds, outcome: "resolved" },
                  degraded,
              };
    }

    /** Legacy resolution, for a flag-off session and for a chain with no object in it. */
    private async resolveSelectionWithoutNodeClass(
        id: ObjectId,
        mode: SelectionResolutionMode,
        signal?: AbortSignal,
    ): Promise<SelectionTarget | undefined> {
        if (mode === "last-object") {
            return singleSelectionTarget(id);
        }
        const clicked = await this.getObject(id, signal);
        if (!clicked || signal?.aborted) {
            return undefined;
        }
        return resolveSelectionLegacy({ access: this, clicked, mode, signal });
    }

    /**
     * Every bitmap the query's match set takes: one per term batch, plus one per scan
     * the engine resumed. `undefined` for an abort, which the caller answers with the
     * interface's empty value.
     *
     * A resumed read is not a page — it *widens* the set with matches the previous read
     * never reached — so its answer is another whole set to union, and a search that
     * comes back degraded now has somewhere to go rather than only something to warn
     * about.
     */
    private async matchIdSets(query: PropertyQuery, signal?: AbortSignal): Promise<BimQueryIdSet[] | undefined> {
        const batches = splitWideTerms(query);
        if (batches.length > 1) {
            l.info(`a term wider than ${MAX_PREDICATE_VALUES} values was split across ${batches.length} requests`);
        }

        const sets: BimQueryIdSet[] = [];
        for (const batch of batches) {
            const predicate = queryPredicate(batch);
            if (!predicate) {
                continue;
            }
            let resumeToken: string | undefined;
            for (let read = 1; ; read++) {
                const set = await this.unlessAborted(signal, () =>
                    searchBimQueryIdSet(this.target, {
                        predicate: restPredicate(predicate),
                        resumeToken,
                        signal,
                    }),
                );
                if (!set) {
                    return undefined;
                }
                sets.push(set);
                resumeToken = set.resumeToken;
                if (!resumeToken) {
                    if (set.degraded) {
                        // Nothing left to continue with: the set is short for good.
                        l.warn(`degraded search; results may be incomplete${causes(set)}`);
                    }
                    break;
                }
                if (read >= MAX_RESUMED_READS) {
                    l.warn(
                        `search still incomplete after ${read} resumed reads; results may be incomplete${causes(set)}`,
                    );
                    break;
                }
            }
        }
        return sets;
    }

    /** Every batch of a split query, in order, with overlapping rows yielded once. */
    private async *matchesOf(
        query: PropertyQuery,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        const batches = splitWideTerms(query);
        if (batches.length > 1) {
            l.info(`a term wider than ${MAX_PREDICATE_VALUES} values was split across ${batches.length} requests`);
        }
        // One batch is the common case and needs no bookkeeping. Several can overlap,
        // since a multi-valued property can put one object in two batches, and a caller
        // computing over the result must not see it twice.
        const seen = batches.length > 1 ? new Set<ObjectId>() : undefined;

        for (const batch of batches) {
            const predicate = queryPredicate(batch);
            if (!predicate) {
                continue;
            }
            for await (const row of this.matches(restPredicate(predicate), signal)) {
                if (seen) {
                    if (seen.has(row.id)) {
                        continue;
                    }
                    seen.add(row.id);
                }
                yield row;
            }
        }
    }

    /** Rows for a predicate, paged as the iterator is consumed. */
    private async *matches(
        predicate: SearchPredicate,
        signal?: AbortSignal,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        for (let page = 1; ; page++) {
            const result = await this.unlessAborted(signal, () =>
                searchBimQueryObjects(this.target, {
                    predicate,
                    page,
                    perPage: BIM_QUERY_OBJECT_PAGE_LIMIT,
                    signal,
                }),
            );
            if (!result) {
                return;
            }
            if (result.degraded) {
                // The engine says the read may have covered less than it was asked for.
                // The rows are correct; rows are missing, with nothing to name them.
                l.warn("degraded search; results may be incomplete");
            }
            for (const object of result.items) {
                yield this.reference(object.id, object.path, object.hasChildren);
            }
            if (result.items.length < BIM_QUERY_OBJECT_PAGE_LIMIT) {
                return;
            }
        }
    }

    /** One object, from the scene when it has it and from the index otherwise. */
    private async object(id: ObjectId, signal?: AbortSignal): Promise<ObjectData | undefined> {
        try {
            const fromScene = (await this.view.data?.getObjectMetaData(id)) as ObjectData | undefined;
            if (fromScene) {
                return fromScene;
            }
        } catch {
            // A scene that cannot answer for this id is not a failure — the index may
            // still know it, which is the whole reason this backend has a second source.
        }
        return this.unlessAborted(signal, () => this.indexObject(id));
    }

    /**
     * One object read from the index, memoised per id — the read every panel that opens
     * on a selection makes, and the one this backend repeats most.
     *
     * The read is not given the caller's signal: the entry is shared, so cancelling it
     * for one caller would reject the object for everyone else waiting on the same
     * promise, and a single object GET is not worth that. `AppObjectDB`'s cache over the
     * object DB is the same shape for the same reason.
     */
    private indexObject(id: ObjectId): Promise<ObjectData | undefined> {
        return this.metadataCache.getOrLoadMetadata(id, async () => {
            const object = await getBimQueryObject(this.target, { id });
            return object && toObjectData(object);
        });
    }

    /**
     * A tree row as the rest of the app expects one. `loadMetaData` reaches for the
     * properties through the same {@link object} read — so a caller can open any row
     * without knowing where either half came from.
     */
    private reference(id: ObjectId, path: string, hasChildren: boolean): HierarcicalObjectReference {
        const loadObject = () => this.object(id);
        return {
            id,
            path,
            // `NodeType` in the API is an ambient const enum, which `isolatedModules`
            // cannot read; `types/misc` mirrors its values (see the note there).
            type: nodeType(hasChildren),
            async loadMetaData(): Promise<ObjectData> {
                const object = await loadObject();
                if (!object) {
                    throw new Error(`Object ${id} has no metadata available`);
                }
                return object;
            },
        };
    }

    /**
     * The interface's abort contract, applied to a request.
     *
     * `ObjectDataAccess` promises that an aborted call answers with its own empty value
     * rather than rejecting — but a signal handed to `fetch` cancels the request *by*
     * rejecting it, and cancelling the request is the whole point of forwarding a signal
     * to a backend that makes them. So the rejection is turned back into the empty answer
     * here: `undefined` for the page or object the caller was waiting on, which each
     * method maps onto its own empty. Anything that is not the abort still throws.
     */
    private async unlessAborted<T>(signal: AbortSignal | undefined, work: () => Promise<T>): Promise<T | undefined> {
        if (signal?.aborted) {
            return undefined;
        }
        try {
            return await work();
        } catch (e) {
            if (signal?.aborted) {
                return undefined;
            }
            throw e;
        }
    }
}
